import {
  buildBreadboardConnectivityModel,
  buildEndpointId,
  getBreadboardRailRole,
  getPrimaryBreadboardId,
  isBreadboardGroundRailHole,
  isBreadboardPositiveRailHole,
  isBreadboardTerminalHole,
  parseEndpointId,
} from './breadboardConnectivity';
import {
  getBreadboardPinInfo,
  isBreadboardComponent,
} from '../../utils/projectBuilderBreadboard';

const validResult = (message = '') => ({
  valid: true,
  code: 'valid',
  message,
  relatedElementIds: [],
});

const invalidResult = (code, message, relatedElementIds = []) => ({
  valid: false,
  code,
  message,
  relatedElementIds,
});

const asRules = (ruleOrRules) => (
  Array.isArray(ruleOrRules) ? ruleOrRules : [ruleOrRules].filter(Boolean)
);

const DEFAULT_ARDUINO_LED_SIGNAL_PIN_KEYS = Object.freeze([
  'D10 PWM/SS',
  'D10',
  '10',
  '21',
  'connector53pin',
]);
const DEFAULT_ARDUINO_LED_GROUND_PIN_KEYS = Object.freeze([
  'GND',
  'GND__11',
  'GND__17',
  'GND__24',
  'GND__40',
  'GND__41',
]);

const asStringList = (value) => (
  (Array.isArray(value) ? value : [value])
    .map((item) => String(item || '').trim())
    .filter(Boolean)
);

const getUniqueStringList = (...values) => (
  Array.from(new Set(values.flatMap(asStringList)))
);

const normalizePassiveType = (component) => String(
  component?.passiveType ||
  component?.templateType ||
  component?.passiveKey ||
  ''
).trim();

const matchesComponentSelector = (component, selector = {}) => {
  if (!component) {
    return false;
  }

  if (selector.componentId && String(component.id) !== String(selector.componentId)) {
    return false;
  }

  if (selector.componentType) {
    const requestedType = String(selector.componentType).trim();
    const passiveType = normalizePassiveType(component);

    if (
      String(component.type) !== requestedType &&
      passiveType !== requestedType &&
      String(component.passiveKey || '') !== requestedType
    ) {
      return false;
    }
  }

  if (selector.passiveType && normalizePassiveType(component) !== String(selector.passiveType)) {
    return false;
  }

  if (selector.passiveKey && String(component.passiveKey || '') !== String(selector.passiveKey)) {
    return false;
  }

  return true;
};

export const findLessonComponents = (projectState = {}, selector = {}) => (
  (Array.isArray(projectState.components) ? projectState.components : [])
    .filter((component) => matchesComponentSelector(component, selector))
);

const findFirstLessonComponent = (projectState = {}, selector = {}) => (
  findLessonComponents(projectState, selector)[0] || null
);

const getTerminalNetId = (model, component, pinKey) => {
  if (!component || !pinKey) {
    return '';
  }

  return model.getEndpointNetId(buildEndpointId(component.id, pinKey));
};

const netContainsBreadboardHole = (model, netId) => {
  const net = model.getNetById(netId);
  return Boolean(net?.holeIds?.length);
};

const getComponentTerminalNetIds = (projectState, model, selector, terminalKeys) => {
  const component = findFirstLessonComponent(projectState, selector);

  if (!component) {
    return {
      component: null,
      netIds: [],
    };
  }

  return {
    component,
    netIds: terminalKeys.map((pinKey) => getTerminalNetId(model, component, pinKey)),
  };
};

const getHoleIdFromRule = (rule, projectState) => {
  const breadboardId = rule.breadboardId || getPrimaryBreadboardId(projectState);
  const pinKey = rule.pinKey || rule.holePinKey;

  if (!breadboardId || !pinKey) {
    return '';
  }

  return buildEndpointId(breadboardId, pinKey);
};

const getSelectedHole = (lessonState = {}) => lessonState.selectedHole || null;

const validateSelectionRule = (rule, context) => {
  const selectedHole = getSelectedHole(context.lessonState);
  const pinKey = selectedHole?.pinKey || parseEndpointId(selectedHole?.holeId).pinKey;
  const info = getBreadboardPinInfo(pinKey);

  if (!selectedHole || !pinKey || !info) {
    if (rule.target === 'terminal-hole') {
      return invalidResult('selection-required', 'Click one of the outlined holes in the main terminal area.');
    }

    return invalidResult('selection-required', 'Click one of the highlighted breadboard holes.');
  }

  switch (rule.target) {
    case 'terminal-hole':
      return isBreadboardTerminalHole(pinKey)
        ? validResult('Breadboard terminal hole selected.')
        : invalidResult('not-terminal-hole', 'Choose a hole in the main terminal strip, not a power rail.');

    case 'positive-rail-hole':
      return isBreadboardPositiveRailHole(pinKey)
        ? validResult('Positive rail selected.')
        : invalidResult('not-positive-rail', 'Choose a hole on a red positive power rail.');

    case 'ground-rail-hole':
      return isBreadboardGroundRailHole(pinKey)
        ? validResult('Ground rail selected.')
        : invalidResult('not-ground-rail', 'Choose a hole on a blue ground rail.');

    case 'opposite-terminal-half': {
      const referenceSelectionId = rule.referenceSelectionId || 'explore-terminal';
      const referenceHole = context.lessonState?.selections?.[referenceSelectionId];
      const referencePinKey = referenceHole?.pinKey || parseEndpointId(referenceHole?.holeId).pinKey;
      const referenceInfo = getBreadboardPinInfo(referencePinKey);

      if (!referenceInfo || !isBreadboardTerminalHole(referencePinKey)) {
        return invalidResult('reference-missing', 'Choose a terminal-strip hole in the previous step first.');
      }

      const isOppositeHalf = (
        isBreadboardTerminalHole(pinKey) &&
        info.column === referenceInfo.column &&
        info.rowGroup !== referenceInfo.rowGroup
      );

      return isOppositeHalf
        ? validResult('Opposite side selected.')
        : invalidResult('not-opposite-side', 'Choose a hole directly across the center trench in the same numbered column.');
    }

    case 'any-hole':
    default:
      return validResult('Breadboard hole selected.');
  }
};

const validateComponentExistsRule = (rule, context) => {
  const selector = {
    componentId: rule.componentId,
    componentType: rule.componentType,
    passiveType: rule.passiveType,
    passiveKey: rule.passiveKey,
  };
  const exists = findLessonComponents(context.projectState, selector).length > 0;

  return exists
    ? validResult('Component exists.')
    : invalidResult(
        'component-missing',
        rule.message || `Add ${rule.componentType || rule.passiveType || 'the required component'} to the workspace.`
      );
};

const validateComponentTerminalInHoleRule = (rule, context) => {
  const model = context.connectivityModel;
  const { component, netIds } = getComponentTerminalNetIds(
    context.projectState,
    model,
    rule.componentSelector || {
      componentId: rule.componentId,
      componentType: rule.componentType,
      passiveType: rule.passiveType,
    },
    [rule.terminalKey || rule.pinKey]
  );

  if (!component) {
    return invalidResult('component-missing', 'Add the required component first.');
  }

  const terminalNetId = netIds[0];

  if (!terminalNetId || !netContainsBreadboardHole(model, terminalNetId)) {
    return invalidResult(
      'terminal-not-in-hole',
      rule.message || `${component.name || 'The component'} terminal is not snapped into a breadboard hole yet.`
    );
  }

  return validResult('Terminal is in a breadboard hole.');
};

const validateComponentTerminalsInDifferentNetsRule = (rule, context) => {
  const terminalKeys = rule.terminalKeys || ['A', 'B'];
  const { component, netIds } = getComponentTerminalNetIds(
    context.projectState,
    context.connectivityModel,
    rule.componentSelector || {
      componentId: rule.componentId,
      componentType: rule.componentType,
      passiveType: rule.passiveType,
    },
    terminalKeys
  );

  if (!component) {
    return invalidResult('component-missing', 'Add the required component first.');
  }

  if (netIds.some((netId) => !netId)) {
    return invalidResult('terminal-not-connected', 'Place both component terminals into breadboard holes.');
  }

  const uniqueNetIds = new Set(netIds);

  return uniqueNetIds.size === netIds.length
    ? validResult('Component terminals are in different nets.')
    : invalidResult(
        'terminals-same-net',
        rule.message || 'Both component leads are in the same connected row. Move one lead to a different electrical node.'
      );
};

const validateHolesConnectedRule = (rule, context) => {
  const model = context.connectivityModel;
  const leftHoleId = rule.holeA || getHoleIdFromRule(rule.left || {}, context.projectState);
  const rightHoleId = rule.holeB || getHoleIdFromRule(rule.right || {}, context.projectState);
  const leftNetId = model.getEndpointNetId(leftHoleId);
  const rightNetId = model.getEndpointNetId(rightHoleId);

  return leftNetId && rightNetId && leftNetId === rightNetId
    ? validResult('Holes are connected.')
    : invalidResult('holes-not-connected', rule.message || 'Those two holes are not electrically connected yet.');
};

const validateNetsSeparatedRule = (rule, context) => {
  const model = context.connectivityModel;
  const leftHoleId = rule.holeA || getHoleIdFromRule(rule.left || {}, context.projectState);
  const rightHoleId = rule.holeB || getHoleIdFromRule(rule.right || {}, context.projectState);
  const leftNetId = model.getEndpointNetId(leftHoleId);
  const rightNetId = model.getEndpointNetId(rightHoleId);

  return leftNetId && rightNetId && leftNetId !== rightNetId
    ? validResult('Nets are separated.')
    : invalidResult('nets-not-separated', rule.message || 'These points are still part of the same electrical node.');
};

const validateWireConnectsRule = (rule, context) => {
  const connections = Array.isArray(context.projectState.connections)
    ? context.projectState.connections
    : [];
  const fromId = buildEndpointId(rule.from?.partId, rule.from?.pinKey);
  const toId = buildEndpointId(rule.to?.partId, rule.to?.pinKey);
  const found = connections.some((connection) => {
    const connectionFromId = buildEndpointId(connection?.from?.partId, connection?.from?.pinKey);
    const connectionToId = buildEndpointId(connection?.to?.partId, connection?.to?.pinKey);

    return (
      (connectionFromId === fromId && connectionToId === toId) ||
      (connectionFromId === toId && connectionToId === fromId)
    );
  });

  return found
    ? validResult('Wire exists.')
    : invalidResult('wire-missing', rule.message || 'Add the requested jumper wire.');
};

const findPowerSource = (projectState) => (
  findFirstLessonComponent(projectState, { componentType: 'dc_power' }) ||
  findFirstLessonComponent(projectState, { passiveType: 'dc_power' })
);

const netHasHoleMatching = (model, netId, predicate) => {
  const net = model.getNetById(netId);

  return Boolean(net?.holeIds?.some((holeId) => {
    const { pinKey } = parseEndpointId(holeId);
    return predicate(pinKey, holeId);
  }));
};

const validatePowerConnectedRule = (rule, context) => {
  const model = context.connectivityModel;
  const source = findPowerSource(context.projectState);

  if (!source) {
    return invalidResult('power-source-missing', 'The lesson needs a 5 V power source.');
  }

  const positiveNetId = getTerminalNetId(model, source, '+');
  const groundNetId = getTerminalNetId(model, source, '-');

  if (!positiveNetId || !groundNetId) {
    return invalidResult('supply-not-connected', 'Connect both terminals of the 5 V source.');
  }

  if (positiveNetId === groundNetId) {
    return invalidResult('power-short', 'The positive and ground terminals are connected together. Remove the short circuit before continuing.');
  }

  const positiveOnRail = netHasHoleMatching(model, positiveNetId, isBreadboardPositiveRailHole);
  const groundOnRail = netHasHoleMatching(model, groundNetId, isBreadboardGroundRailHole);

  if (!positiveOnRail && !groundOnRail) {
    return invalidResult('rails-not-connected', 'Connect positive to a red rail and negative to a blue ground rail.');
  }

  if (!positiveOnRail) {
    return invalidResult('positive-rail-missing', 'Connect the positive terminal to a red positive rail.');
  }

  if (!groundOnRail) {
    return invalidResult('ground-rail-missing', 'Connect the negative terminal to a blue ground rail.');
  }

  return validResult('Power rails are connected.');
};

const getFirstTwoTerminalNets = (projectState, model, selector, terminalKeys) => {
  const component = findFirstLessonComponent(projectState, selector);

  if (!component) {
    return null;
  }

  const [firstKey, secondKey] = terminalKeys;

  return {
    component,
    firstKey,
    secondKey,
    firstNetId: getTerminalNetId(model, component, firstKey),
    secondNetId: getTerminalNetId(model, component, secondKey),
  };
};

const findArduinoBoardForRule = (projectState = {}, rule = {}) => {
  if (rule.boardSelector || rule.arduinoSelector) {
    const selectedBoard = findFirstLessonComponent(
      projectState,
      rule.boardSelector || rule.arduinoSelector
    );

    if (selectedBoard) {
      return selectedBoard;
    }
  }

  const componentId = String(
    rule.boardComponentId ||
    rule.arduinoComponentId ||
    rule.componentId ||
    ''
  ).trim();
  const boardId = String(rule.boardId || rule.arduinoBoardId || '').trim();
  const components = Array.isArray(projectState.components) ? projectState.components : [];

  if (componentId || boardId) {
    const matchedBoard = components.find((component) => (
      component?.type === 'board' &&
      (
        (componentId && String(component.id) === componentId) ||
        (boardId && String(component.id) === boardId) ||
        (boardId && String(component.boardId || component.board?.id || '') === boardId)
      )
    ));

    if (matchedBoard) {
      return matchedBoard;
    }
  }

  return components.find((component) => component?.type === 'board') || null;
};

const getArduinoSignalPinKeysForRule = (rule = {}) => {
  const configuredKeys = getUniqueStringList(
    rule.signalPinKeys,
    rule.digitalPinKeys,
    rule.arduinoPinKeys,
    rule.signalPinKey,
    rule.digitalPinKey,
    rule.arduinoPinKey,
    rule.pinKey
  );

  if (configuredKeys.length > 0) {
    return configuredKeys;
  }

  const digitalPin = String(rule.digitalPin || '').trim();

  if (digitalPin) {
    const normalizedDigitalPin = digitalPin.replace(/^D/i, '');

    return getUniqueStringList(
      `D${normalizedDigitalPin}`,
      normalizedDigitalPin === '10' ? DEFAULT_ARDUINO_LED_SIGNAL_PIN_KEYS : []
    );
  }

  return [...DEFAULT_ARDUINO_LED_SIGNAL_PIN_KEYS];
};

const getArduinoGroundPinKeysForRule = (rule = {}) => {
  const configuredKeys = getUniqueStringList(
    rule.groundPinKeys,
    rule.gndPinKeys,
    rule.groundPinKey,
    rule.gndPinKey
  );

  return configuredKeys.length > 0
    ? configuredKeys
    : [...DEFAULT_ARDUINO_LED_GROUND_PIN_KEYS];
};

const getFirstComponentPinNet = (model, component, pinKeys = []) => {
  for (const pinKey of pinKeys) {
    const netId = getTerminalNetId(model, component, pinKey);

    if (netId) {
      return {
        pinKey,
        netId,
      };
    }
  }

  return {
    pinKey: pinKeys[0] || '',
    netId: '',
  };
};

const getComponentPinNetIds = (model, component, pinKeys = []) => (
  new Set(
    pinKeys
      .map((pinKey) => getTerminalNetId(model, component, pinKey))
      .filter(Boolean)
  )
);

const validateArduinoLedPinCircuitRule = (rule, context) => {
  const model = context.connectivityModel;
  const projectState = context.projectState;
  const board = findArduinoBoardForRule(projectState, rule);
  const led = getFirstTwoTerminalNets(
    projectState,
    model,
    rule.ledSelector || {
      componentId: rule.ledComponentId,
      componentType: 'led',
    },
    [
      rule.ledAnodeKey || rule.anodeTerminalKey || 'A',
      rule.ledCathodeKey || rule.cathodeTerminalKey || 'K',
    ]
  );
  const resistor = getFirstTwoTerminalNets(
    projectState,
    model,
    rule.resistorSelector || {
      componentId: rule.resistorComponentId,
      componentType: 'resistor',
    },
    rule.resistorTerminalKeys || ['A', 'B']
  );

  if (!board) {
    return invalidResult('arduino-missing', 'Add the Arduino Uno board first.');
  }

  if (!led?.component) {
    return invalidResult('led-missing', 'Add an LED to the circuit.');
  }

  if (!resistor?.component) {
    return invalidResult('resistor-missing', 'Add a current-limiting resistor.');
  }

  if (!led.firstNetId || !led.secondNetId) {
    return invalidResult('led-open', 'Place both LED legs into connected breadboard holes.');
  }

  if (!resistor.firstNetId || !resistor.secondNetId) {
    return invalidResult('resistor-open', 'Place both resistor leads into connected breadboard holes.');
  }

  if (led.firstNetId === led.secondNetId) {
    return invalidResult('led-same-net', 'Both LED legs are in the same electrical node.');
  }

  if (resistor.firstNetId === resistor.secondNetId) {
    return invalidResult('resistor-same-net', 'The resistor leads are in the same electrical node.');
  }

  const signalPinKeys = getArduinoSignalPinKeysForRule(rule);
  const groundPinKeys = getArduinoGroundPinKeysForRule(rule);
  const signalPin = getFirstComponentPinNet(model, board, signalPinKeys);
  const groundNetIds = getComponentPinNetIds(model, board, groundPinKeys);

  if (!signalPin.netId) {
    return invalidResult(
      'arduino-signal-open',
      `Wire the LED anode to Arduino ${signalPin.pinKey || 'the selected digital pin'}.`
    );
  }

  if (groundNetIds.size === 0) {
    return invalidResult('arduino-ground-open', 'Wire the resistor back to one of the Arduino GND pins.');
  }

  if (groundNetIds.has(signalPin.netId)) {
    return invalidResult('direct-short', 'Arduino D10 and GND are connected together. Remove the short.');
  }

  const anodeNetId = led.firstNetId;
  const cathodeNetId = led.secondNetId;

  if (cathodeNetId === signalPin.netId && groundNetIds.has(anodeNetId)) {
    return invalidResult('led-reversed', 'The LED is reversed. Connect the anode to Arduino D10 and the cathode toward the resistor.');
  }

  if (anodeNetId !== signalPin.netId) {
    return invalidResult(
      'anode-not-signal',
      `Wire the LED anode to Arduino ${signalPin.pinKey || 'D10'}.`
    );
  }

  const resistorSharesCathode = (
    resistor.firstNetId === cathodeNetId ||
    resistor.secondNetId === cathodeNetId
  );

  if (!resistorSharesCathode) {
    return invalidResult('resistor-not-on-cathode', 'Connect one resistor lead to the LED cathode node.');
  }

  const resistorOtherNetId = resistor.firstNetId === cathodeNetId
    ? resistor.secondNetId
    : resistor.firstNetId;

  if (!groundNetIds.has(resistorOtherNetId)) {
    return invalidResult('resistor-not-grounded', 'Connect the other resistor lead to any Arduino GND pin.');
  }

  return validResult('LED anode is on Arduino D10, and the cathode returns to GND through the resistor.');
};

export const analyzeLessonLedCircuit = (projectState = {}) => {
  const model = buildBreadboardConnectivityModel(projectState);
  const source = findPowerSource(projectState);
  const led = getFirstTwoTerminalNets(
    projectState,
    model,
    { componentType: 'led' },
    ['A', 'K']
  );
  const resistor = getFirstTwoTerminalNets(
    projectState,
    model,
    { componentType: 'resistor' },
    ['A', 'B']
  );

  if (!source) {
    return invalidResult('power-source-missing', 'The 5 V source is missing.');
  }

  const positiveNetId = getTerminalNetId(model, source, '+');
  const groundNetId = getTerminalNetId(model, source, '-');

  if (!positiveNetId || !groundNetId) {
    return invalidResult('supply-not-connected', 'Connect the 5 V source to the power rails first.');
  }

  if (positiveNetId === groundNetId) {
    return invalidResult('direct-short', 'Positive and ground are connected together. Remove the short circuit.');
  }

  if (!led?.component) {
    return invalidResult('led-missing', 'Add an LED to the breadboard.');
  }

  if (!resistor?.component) {
    return invalidResult('resistor-missing', 'Add a current-limiting resistor.');
  }

  if (!led.firstNetId || !led.secondNetId) {
    return invalidResult('led-open', 'Place both LED legs into breadboard holes.');
  }

  if (!resistor.firstNetId || !resistor.secondNetId) {
    return invalidResult('resistor-open', 'Place both resistor leads into breadboard holes.');
  }

  if (led.firstNetId === led.secondNetId) {
    return invalidResult('led-same-net', 'Both LED legs are in the same electrical node.');
  }

  if (resistor.firstNetId === resistor.secondNetId) {
    return invalidResult('resistor-same-net', 'The resistor leads are in the same connected row, so it is bypassed.');
  }

  const resistorNets = [resistor.firstNetId, resistor.secondNetId];
  const resistorOtherNet = (netId) => (
    resistor.firstNetId === netId ? resistor.secondNetId :
      resistor.secondNetId === netId ? resistor.firstNetId :
        ''
  );

  const resistorOnAnodeSide = (
    resistorNets.includes(positiveNetId) &&
    resistorOtherNet(positiveNetId) === led.firstNetId &&
    led.secondNetId === groundNetId
  );
  const resistorOnCathodeSide = (
    led.firstNetId === positiveNetId &&
    resistorNets.includes(groundNetId) &&
    resistorOtherNet(groundNetId) === led.secondNetId
  );

  if (resistorOnAnodeSide || resistorOnCathodeSide) {
    return validResult('Complete LED circuit detected.');
  }

  const reversedWithResistor = (
    (
      resistorNets.includes(positiveNetId) &&
      resistorOtherNet(positiveNetId) === led.secondNetId &&
      led.firstNetId === groundNetId
    ) ||
    (
      led.secondNetId === positiveNetId &&
      resistorNets.includes(groundNetId) &&
      resistorOtherNet(groundNetId) === led.firstNetId
    )
  );

  if (reversedWithResistor) {
    return invalidResult('led-reversed', 'The LED polarity is reversed. Put the anode toward 5 V and the cathode toward ground.');
  }

  if (led.firstNetId === positiveNetId && led.secondNetId === groundNetId) {
    return invalidResult('resistor-bypassed', 'The LED is connected across power without the resistor in series.');
  }

  const touchesPositive = [led.firstNetId, led.secondNetId, ...resistorNets].includes(positiveNetId);
  const touchesGround = [led.firstNetId, led.secondNetId, ...resistorNets].includes(groundNetId);

  if (!touchesGround) {
    return invalidResult('missing-ground', 'The circuit does not return to ground yet.');
  }

  if (!touchesPositive) {
    return invalidResult('missing-positive', 'The circuit is not connected to the positive supply yet.');
  }

  return invalidResult('open-circuit', 'There is still an open point in the path through the resistor and LED.');
};

const validateResistorInSeriesWithLedRule = (rule, context) => {
  const model = context.connectivityModel;
  const led = getFirstTwoTerminalNets(
    context.projectState,
    model,
    { componentType: 'led' },
    ['A', 'K']
  );
  const resistor = getFirstTwoTerminalNets(
    context.projectState,
    model,
    { componentType: 'resistor' },
    ['A', 'B']
  );

  if (!led?.component) {
    return invalidResult('led-missing', 'Place the LED first.');
  }

  if (!resistor?.component) {
    return invalidResult('resistor-missing', 'Add a resistor.');
  }

  if (!led.firstNetId || !led.secondNetId || !resistor.firstNetId || !resistor.secondNetId) {
    return invalidResult('component-open', 'Place both LED legs and both resistor leads into breadboard holes.');
  }

  if (led.firstNetId === led.secondNetId) {
    return invalidResult('led-same-net', 'Both LED legs are currently connected to the same breadboard strip. Move one leg to a different row.');
  }

  if (resistor.firstNetId === resistor.secondNetId) {
    return invalidResult('resistor-same-net', 'The resistor leads are in the same connected row, so it is not in series with the LED.');
  }

  const sharesLedTerminal = [led.firstNetId, led.secondNetId].some((ledNetId) => (
    ledNetId === resistor.firstNetId || ledNetId === resistor.secondNetId
  ));

  return sharesLedTerminal
    ? validResult('Resistor shares one node with the LED.')
    : invalidResult('resistor-not-series', 'Connect one resistor lead to one LED leg, then put the other resistor lead on a different row.');
};

const validatePowerStateRule = (rule, context) => {
  const model = context.connectivityModel;
  const endpointId = rule.endpointId || getHoleIdFromRule(rule, context.projectState);
  const powerState = model.getNetForEndpoint(endpointId)?.powerState || 'unpowered';

  return powerState === rule.state
    ? validResult(`Power state is ${rule.state}.`)
    : invalidResult('power-state-mismatch', rule.message || `Expected ${rule.state}, but this node is ${powerState}.`);
};

export const classifyTraceSelection = (projectState = {}, holeDescriptor = {}) => {
  const holeId = normalizeHoleDescriptorToId(holeDescriptor);
  const { pinKey } = parseEndpointId(holeId);
  const model = buildBreadboardConnectivityModel(projectState);
  const net = model.getNetForEndpoint(holeId);

  if (!net) {
    return '';
  }

  if (net.powerState === 'positive' || isBreadboardPositiveRailHole(pinKey)) {
    return 'positive';
  }

  if (net.powerState === 'ground' || isBreadboardGroundRailHole(pinKey)) {
    return 'ground';
  }

  const led = getFirstTwoTerminalNets(projectState, model, { componentType: 'led' }, ['A', 'K']);
  const resistor = getFirstTwoTerminalNets(projectState, model, { componentType: 'resistor' }, ['A', 'B']);
  const ledNets = [led?.firstNetId, led?.secondNetId].filter(Boolean);
  const resistorNets = [resistor?.firstNetId, resistor?.secondNetId].filter(Boolean);
  const isJunction = ledNets.some((netId) => netId === net.id) &&
    resistorNets.some((netId) => netId === net.id);

  if (isJunction) {
    return 'resistor-led-junction';
  }

  const railRole = getBreadboardRailRole(pinKey);
  return railRole || 'node';
};

const normalizeHoleDescriptorToId = (holeDescriptor) => (
  typeof holeDescriptor === 'string'
    ? holeDescriptor
    : buildEndpointId(
        holeDescriptor?.breadboardId || holeDescriptor?.partId,
        holeDescriptor?.pinKey
      )
);

const validateTraceInspectedRule = (rule, context) => {
  if (rule.requireEnabled && !context.lessonState?.traceConnectionsEnabled) {
    return invalidResult('trace-not-enabled', 'Click Trace Connections first.');
  }

  const inspectedTargets = new Set(context.lessonState?.inspectedTargets || []);
  const missingTargets = (rule.targets || []).filter((target) => !inspectedTargets.has(target));

  return missingTargets.length === 0
    ? validResult('Required nets inspected.')
    : invalidResult(
        'trace-targets-missing',
        rule.message || `Inspect ${missingTargets.join(', ')} before finishing.`
      );
};

const getProjectSimulationCode = (projectState = {}) => String(
  projectState?.simulation?.code ||
  projectState?.simulationCode ||
  ''
);

const normalizeCodeSource = (source, options = {}) => {
  let normalizedSource = String(source || '');

  if (options.ignoreWhitespace) {
    normalizedSource = normalizedSource.replace(/\s+/gu, '');
  }

  if (options.caseSensitive === false) {
    normalizedSource = normalizedSource.toLowerCase();
  }

  return normalizedSource;
};

const validateCodeViewedRule = (rule, context) => (
  context.lessonState?.codeViewed || context.lessonState?.codePanelOpen
    ? validResult(rule.successMessage || 'Code panel opened.')
    : invalidResult(
        'code-not-viewed',
        rule.message || 'Open the Arduino code panel before continuing.'
      )
);

const validateCodeEditedRule = (rule, context) => {
  if (context.lessonState?.codeEdited) {
    return validResult(rule.successMessage || 'Sketch edited.');
  }

  const currentCode = getProjectSimulationCode(context.projectState);
  const starterCode = getProjectSimulationCode(context.lesson?.initialProject || {});

  if (
    rule.acceptChangedSource !== false &&
    starterCode.trim() &&
    currentCode.trim() &&
    currentCode !== starterCode
  ) {
    return validResult(rule.successMessage || 'Sketch edited.');
  }

  return invalidResult(
    'code-not-edited',
    rule.message || 'Edit the Arduino sketch before continuing.'
  );
};

const validateCodeRunRule = (rule, context) => {
  const runState = context.lessonState?.codeRun || {};
  const currentStatus = String(
    runState.status ||
    (context.lessonState?.simulationPhase === 'running' ? 'running' : '')
  ).trim();
  const acceptedStatuses = getUniqueStringList(rule.acceptStatuses, rule.statuses);
  const defaultAcceptedStatuses = rule.requireSuccess === false
    ? ['compiling', 'running', 'success', 'error']
    : ['running', 'success'];
  const validStatuses = acceptedStatuses.length > 0
    ? acceptedStatuses
    : defaultAcceptedStatuses;

  if (currentStatus && validStatuses.includes(currentStatus)) {
    return validResult(rule.successMessage || 'Sketch run completed.');
  }

  if (currentStatus === 'stale') {
    return invalidResult(
      'code-run-stale',
      rule.message || 'Run the Arduino sketch again after editing it.'
    );
  }

  if (currentStatus === 'error') {
    return invalidResult(
      'code-run-failed',
      rule.message || runState.message || 'The sketch did not run successfully yet.'
    );
  }

  return invalidResult(
    'code-not-run',
    rule.message || 'Run the Arduino sketch before continuing.'
  );
};

const validateCodeContainsRule = (rule, context) => {
  const expectedSnippets = getUniqueStringList(
    rule.contains,
    rule.text,
    rule.value,
    rule.snippet
  );

  if (expectedSnippets.length === 0) {
    return validResult(rule.successMessage || 'No code snippets required.');
  }

  const source = normalizeCodeSource(getProjectSimulationCode(context.projectState), rule);
  const missingSnippets = expectedSnippets.filter((snippet) => (
    !source.includes(normalizeCodeSource(snippet, rule))
  ));

  return missingSnippets.length === 0
    ? validResult(rule.successMessage || 'Sketch contains the required code.')
    : invalidResult(
        'code-missing-snippet',
        rule.message || `Add ${missingSnippets[0]} to the Arduino sketch.`
      );
};

const validateCodeRegexRule = (rule, context) => {
  const pattern = String(rule.pattern || rule.regex || '').trim();

  if (!pattern) {
    return validResult(rule.successMessage || 'No code pattern required.');
  }

  let expression = null;

  try {
    expression = new RegExp(pattern, String(rule.flags || ''));
  } catch (error) {
    return invalidResult(
      'code-regex-invalid',
      rule.message || error?.message || 'The code validation pattern is invalid.'
    );
  }

  return expression.test(getProjectSimulationCode(context.projectState))
    ? validResult(rule.successMessage || 'Sketch matches the required pattern.')
    : invalidResult(
        'code-pattern-missing',
        rule.message || 'Update the Arduino sketch to match the required code pattern.'
      );
};

const validateRule = (rule, context) => {
  if (!rule) {
    return validResult();
  }

  switch (rule.type) {
    case 'all': {
      const results = asRules(rule.rules).map((childRule) => validateRule(childRule, context));
      const failed = results.find((result) => !result.valid);
      return failed || validResult(rule.message || 'All conditions passed.');
    }

    case 'any': {
      const results = asRules(rule.rules).map((childRule) => validateRule(childRule, context));
      const passed = results.find((result) => result.valid);
      return passed || results[0] || invalidResult('any-failed', rule.message || 'Complete one of the requested actions.');
    }

    case 'not': {
      const result = validateRule(rule.rule, context);
      return result.valid
        ? invalidResult(rule.code || 'not-rule-failed', rule.message || 'This condition should not be true.')
        : validResult(rule.successMessage || 'Condition is not present.');
    }

    case 'selection':
      return validateSelectionRule(rule, context);

    case 'component-exists':
      return validateComponentExistsRule(rule, context);

    case 'component-terminal-in-hole':
      return validateComponentTerminalInHoleRule(rule, context);

    case 'component-terminals-in-different-nets':
      return validateComponentTerminalsInDifferentNetsRule(rule, context);

    case 'holes-connected':
      return validateHolesConnectedRule(rule, context);

    case 'nets-separated':
      return validateNetsSeparatedRule(rule, context);

    case 'wire-connects':
      return validateWireConnectsRule(rule, context);

    case 'power-connected':
      return validatePowerConnectedRule(rule, context);

    case 'power-state':
      return validatePowerStateRule(rule, context);

    case 'resistor-in-series-with-led':
      return validateResistorInSeriesWithLedRule(rule, context);

    case 'arduino-led-pin-circuit':
      return validateArduinoLedPinCircuitRule(rule, context);

    case 'led-circuit':
      return analyzeLessonLedCircuit(context.projectState);

    case 'trace-inspected':
      return validateTraceInspectedRule(rule, context);

    case 'code-viewed':
      return validateCodeViewedRule(rule, context);

    case 'code-edited':
      return validateCodeEditedRule(rule, context);

    case 'code-run':
      return validateCodeRunRule(rule, context);

    case 'code-contains':
      return validateCodeContainsRule(rule, context);

    case 'code-regex':
      return validateCodeRegexRule(rule, context);

    case 'noop':
    default:
      return validResult(rule.successMessage || '');
  }
};

export const validateLessonStep = ({
  lesson,
  step,
  projectState = {},
  lessonState = {},
} = {}) => {
  if (!lesson || !step) {
    return invalidResult('lesson-missing', 'Lesson step is unavailable.');
  }

  const connectivityModel = buildBreadboardConnectivityModel(projectState);
  const context = {
    lesson,
    step,
    projectState,
    lessonState,
    connectivityModel,
  };
  const result = validateRule(step.validation || { type: 'noop' }, context);

  if (result.valid) {
    return {
      ...result,
      message: step.successMessage || result.message || '',
    };
  }

  const correction = (step.correctionMessages || []).find((entry) => (
    entry.code === result.code
  ));

  return {
    ...result,
    message: correction?.message || result.message || 'Try that step again.',
  };
};

export const getLessonStepById = (lesson, stepId) => (
  (lesson?.steps || []).find((step) => step.id === stepId) || null
);

export const getLessonStepIndex = (lesson, stepId) => {
  const index = (lesson?.steps || []).findIndex((step) => step.id === stepId);
  return index >= 0 ? index : 0;
};

export const getLessonStepActionHints = (step) => (
  Array.isArray(step?.hints) ? step.hints : []
);

export const getBreadboardHoleReference = (holeDescriptor = {}) => {
  const holeId = normalizeHoleDescriptorToId(holeDescriptor);
  const { partId, pinKey } = parseEndpointId(holeId);

  return {
    breadboardId: partId,
    holeId,
    pinKey,
  };
};

export const isLessonBreadboardComponent = isBreadboardComponent;
